import { useRef, useState } from 'react'
import styled, { type CSSProperties } from 'styled-components/macro'

import { Select } from '@loadsmart/loadsmart-ui'
import { type SelectProps } from '@loadsmart/loadsmart-ui/dist/components/Select/Select.types'
import { type Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable'
import type EventLike from '@loadsmart/loadsmart-ui/dist/utils/types/EventLike'
import { Field, TextField, type WCTextField } from '@loadsmart/miranda-react'
import { toCSSValue } from '@loadsmart/miranda-react/dist/tokens'

import { type Option } from '../../../domain/Option'
import { Icon } from '../icon/Icon'

const InputWrapper = styled.div`
  display: flex;
  gap: 0;
`

const StyledSelect = styled(Select)`
  [role='presentation'] {
    border-radius: ${toCSSValue('border-radius-s')} 0 0 ${toCSSValue('border-radius-s')};
  }
  width: 82px;
  flex-grow: 0;
  flex-shrink: 0;
`

const StyledTextField = styled(TextField)`
  border-radius: 0 ${toCSSValue('border-radius-s')} ${toCSSValue('border-radius-s')} 0;
  border-left: none;
  min-width: 0;
  flex-grow: 1;
  flex-shrink: 1;
`

export type SearchableSelectProps = Omit<
  SelectProps,
  'value' | 'onChange' | 'options' | 'multiple' | 'placeholder'
> & {
  clearAfterSearch?: boolean
  debounce?: number
  label?: string
  options: Array<Option>
  searchPlaceholder?: string
  searchValue?: string
  style?: CSSProperties
  onChange: (option: Option, search: string) => void
}

type State = {
  option: Option
  search: string
}

export function SearchableSelect({
  clearAfterSearch,
  label,
  options,
  searchPlaceholder,
  searchValue,
  style,
  id = 'search-select',
  onChange,
  ...props
}: SearchableSelectProps) {
  const [state, setState] = useState<State>({ option: options[0], search: '' })
  const textFieldRef = useRef<WCTextField>(null)

  const { option, search } = state

  function handleOnChangeSelect(event: EventLike<Selectable | Selectable[] | null>) {
    const newValue = event?.target?.value as Option

    onChange(newValue, search)

    setState(prevState => ({
      option: newValue,
      search: prevState.search,
    }))
  }

  function handleOnChangeSearch(newSearch: string) {
    setState(prevState => ({ ...prevState, search: newSearch }))
  }

  function handleOnKeyUp(code: KeyboardEvent['code']) {
    if (code !== 'Enter') return

    onChange(option, search)

    if (clearAfterSearch) {
      setState(prevState => ({ ...prevState, search: '' }))
      textFieldRef.current?.clear?.()
    }
  }

  return (
    <Field style={style}>
      {label && <Field.Label id={id}>{label}</Field.Label>}
      <InputWrapper>
        <StyledSelect
          id={id}
          value={option}
          multiple={false}
          options={options}
          onChange={handleOnChangeSelect}
          hideClear
          {...props}
        />
        <StyledTextField
          ref={textFieldRef}
          leading={<Icon name="search" />}
          onInput={({ target }) => handleOnChangeSearch((target as HTMLInputElement).value)}
          onKeyUp={({ code }) => handleOnKeyUp(code)}
          placeholder={searchPlaceholder}
          value={searchValue}
        />
      </InputWrapper>
    </Field>
  )
}
